/*This component is specific to personskade claims*/
import React, { useState, useCallback, useEffect, useContext } from 'react';
import { format } from 'date-fns';
import { RadioField, RadioInput, Button, Box, SelectField, DateField, TextField } from '@flos/react-ui';
import { ElementValue } from '../../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../../types/claim-form-submit-field-value';
import { SummaryRowTemplate } from '../../../helpers/templates';
import useAppState from '../../../state/appState';
import { FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { ElementValueHuman } from '../../../types/element-values';
import { getValue } from '../../values/Get';
import { getClaimFormSubmitValue } from '../../../helpers/getClaimFormSubmitValue';
import { getDateObject } from '../../fields/Datepicker';
import { fieldLabel } from './utils';
import { getHospitalList } from '../../../services/hospitals';
import { ConfigContext } from '../../../context/ConfigContext';
import { HospitalList } from '../../../types/services';

type FieldSet = {
    bodyPractitionerType: {
        label: string;
        value: string;
    };
    bodyPractitionerName: {
        label: string;
        value: string;
    };
    bodyPractitionerDate: {
        label: string;
        value: string;
    };
    bodyPractitionerAddress: {
        label: string;
        value: string;
    };
};

type ExaminationValue = {
    fieldSets: FieldSet[];
};

const Examination = ({ elementId, caption }: any) => {
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const topConfig = useContext(ConfigContext);
    const [hospitals, setHospitals] = useState<HospitalList>([]);

    useEffect(() => {
        const fetchHospitals = async () => {
            const response = await getHospitalList(topConfig);
            if (response) {
                setHospitals(response);
            }
        };
        fetchHospitals();
    }, [topConfig]);

    // Dropdown options for practitioner types
    const values: any = [
        { caption: 'Hospital', value: 'HOSPITAL' },
        { caption: 'Egen læge', value: 'DOCTOR' },
        { caption: 'Kiropraktor', value: 'CHIROPRACTOR' },
        { caption: 'Fysioterapeut', value: 'PHYSIOTHERAPIST' },
        { caption: 'Andet', value: 'OTHER' }
    ];

    // Initialize field sets from form values/default values
    const initializeFieldSets = useCallback(() => {
        if (formValues && formValues[elementId] && formValues[elementId].fieldSets) {
            return formValues[elementId].fieldSets;
        }
        return [
            {
                bodyPractitionerType: { label: '', value: '' },
                bodyPractitionerName: { label: '', value: '' },
                bodyPractitionerDate: { label: '', value: '' },
                bodyPractitionerAddress: { label: '', value: '' }
            }
        ];
    }, [formValues, elementId]);

    const [fieldSets, setFieldSets] = useState(initializeFieldSets);

    // Update form values
    const updateFormValues = (updatedFieldSets: any) => {
        setFieldSets(updatedFieldSets);
        setFormValue(elementId, { fieldSets: updatedFieldSets });
    };

    // Add a new field set
    const addItem = () => {
        const newFieldSets = [
            ...fieldSets,
            {
                bodyPractitionerType: { label: '', value: '' },
                bodyPractitionerName: { label: '', value: '' },
                bodyPractitionerDate: { label: '', value: '' },
                bodyPractitionerAddress: { label: '', value: '' }
            }
        ];
        updateFormValues(newFieldSets);
    };

    // Remove a field set by index
    const removeItem = (index: number) => {
        const updatedFieldSets = fieldSets.filter((_: any, i: any) => i !== index);
        updateFormValues(updatedFieldSets);
    };

    // Handle field changes within a field set
    const handleFieldChange = (index: any, field: any, value: any) => {
        const updatedFieldSets = fieldSets.map((set: any, i: any) => (i === index ? { ...set, [field]: value } : set));
        updateFormValues(updatedFieldSets);
    };

    return (
        <fieldset>
            {fieldSets.map((fieldSet: FieldSet, index: number) => (
                <div className="u-spacing-stack-m">
                    <Box theme="secondary" key={index}>
                        <RadioField
                            label={fieldLabel.bodyPractitionerType}
                            id={`bodyPractitionerType-${index}`}
                            key={`bodyPractitionerType-${index}`}
                            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                                const value = evt.target.value;
                                const label = values.find((item: any) => item.value === value)?.caption;
                                handleFieldChange(index, 'bodyPractitionerType', { label, value });
                            }}
                            value={fieldSet.bodyPractitionerType.value}
                            required
                        >
                            {values.map((option: any, key: any) => (
                                <RadioInput key={key} label={option.caption} value={option.value} />
                            ))}
                        </RadioField>
                        <div className="u-spacing-inset-m--stretch-max">
                            {fieldSet.bodyPractitionerType.value === 'HOSPITAL' && (
                                <>
                                    <SelectField
                                        label={fieldLabel.bodyPractitionerHospitalName}
                                        id={`bodyPractitionerName-${index}`}
                                        key={`bodyPractitionerName-${index}`}
                                        options={hospitals}
                                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                            const value = event.target.value;
                                            handleFieldChange(index, 'bodyPractitionerName', {
                                                label: fieldLabel.bodyPractitionerHospitalName,
                                                value
                                            });
                                        }}
                                        required
                                        value={fieldSet.bodyPractitionerName.value}
                                    />
                                </>
                            )}
                            {fieldSet.bodyPractitionerType.value &&
                                fieldSet.bodyPractitionerType.value !== 'HOSPITAL' && (
                                    <>
                                        <TextField
                                            required
                                            value={fieldSet.bodyPractitionerName.value}
                                            id={`bodyPractitionerName-${index}`}
                                            key={`bodyPractitionerName-${index}`}
                                            label={fieldLabel.bodyPractitionerName}
                                            onChange={({ target: { value } }) => {
                                                handleFieldChange(index, 'bodyPractitionerName', {
                                                    label: fieldLabel.bodyPractitionerName,
                                                    value
                                                });
                                            }}
                                        />
                                        {/*  <AddressField
                                            defaultValue={fieldSet.bodyPractitionerAddress.value}
                                            fieldEvent={(data) => {
                                                if (data.address) {
                                                    handleFieldChange(index, 'bodyPractitionerAddress', {
                                                        label: fieldLabel.bodyPractitionerAddress,
                                                        value: data.address
                                                    });
                                                }
                                            }}
                                            id={`bodyPractitionerAddress-${index}`}
                                            key={`bodyPractitionerAddress-${index}`}
                                            label={fieldLabel.bodyPractitionerAddress}
                                            required
                                            aria-label={caption}
                                            data-testid="addressfield"
                                            onChange={({ target: { value } }) => {
                                                handleFieldChange(index, 'bodyPractitionerAddress', {
                                                    label: fieldLabel.bodyPractitionerAddress,
                                                    value
                                                });
                                                if (!value) {
                                                    setFormValue(`bodyPractitionerAddress-${index}`, '');
                                                }
                                            }}
                                        />*/}
                                        <TextField
                                            required
                                            value={fieldSet.bodyPractitionerAddress.value}
                                            id={`bodyPractitionerAddress-${index}`}
                                            key={`bodyPractitionerAddress-${index}`}
                                            label={fieldLabel.bodyPractitionerAddress}
                                            onChange={({ target: { value } }) => {
                                                handleFieldChange(index, 'bodyPractitionerAddress', {
                                                    label: fieldLabel.bodyPractitionerAddress,
                                                    value
                                                });
                                            }}
                                        />
                                    </>
                                )}
                            {fieldSet.bodyPractitionerType.value && (
                                <DateField
                                    dateFormat="dd-MM-yyyy"
                                    selected={getDateObject(fieldSet.bodyPractitionerDate.value)}
                                    onChange={(date: Date | null) => {
                                        if (!date) return;
                                        handleFieldChange(index, 'bodyPractitionerDate', {
                                            label: fieldLabel.bodyPractitionerDate,
                                            value: format(date, 'dd-MM-yyyy')
                                        });
                                    }}
                                    onFocus={(e: any) => e.target.blur()} // Prevent keyboard on mobile - https://stackoverflow.com/a/69217777
                                    id={`bodyPractitionerDate-${index}`}
                                    key={`bodyPractitionerDate-${index}`}
                                    errorText="Vælg dato"
                                    label="Hvornår blev du undersøgt"
                                    required
                                    aria-label={caption}
                                    data-testid="datefield"
                                    maxDate={new Date()}
                                    minDate={new Date(`1970-01-01`)}
                                    wrapperClassName="u-spacing-stack-none"
                                />
                            )}
                        </div>
                        <Button theme="secondary" onClick={() => removeItem(index)}>
                            {' '}
                            Fjern
                        </Button>
                    </Box>
                </div>
            ))}

            <Button onClick={addItem}>Tilføj ny undersøgelse</Button>
        </fieldset>
    );
};

Examination.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const value: ExaminationValue = getValue(formConfig, formValues, elementId);
    if (value === undefined) {
        return <></>;
    }

    const contentSummaryRowTemplate: JSX.Element[] = [];
    const { fieldSets } = value;

    fieldSets.forEach((fieldSet: FieldSet, index: number) => {
        const { bodyPractitionerType, bodyPractitionerName, bodyPractitionerDate, bodyPractitionerAddress } = fieldSet;

        if (bodyPractitionerType.value === 'HOSPITAL') {
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`bodyPractitionerType-${index}`}
                    elementId={elementId}
                    caption={fieldLabel.bodyPractitionerType}
                    value={bodyPractitionerType.label}
                />
            );
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`bodyPractitionerName-${index}`}
                    elementId={elementId}
                    caption={bodyPractitionerName.label}
                    value={bodyPractitionerName.value}
                />
            );
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`bodyPractitionerDate-${index}`}
                    elementId={elementId}
                    caption={bodyPractitionerDate.label}
                    value={bodyPractitionerDate.value}
                />
            );
        } else {
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`bodyPractitionerType-${index}`}
                    elementId={elementId}
                    caption={fieldLabel.bodyPractitionerType}
                    value={bodyPractitionerType.label}
                />
            );
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`bodyPractitionerName-${index}`}
                    elementId={elementId}
                    caption={bodyPractitionerName.label}
                    value={bodyPractitionerName.value}
                />
            );
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`bodyPractitionerAddress-${index}`}
                    elementId={elementId}
                    caption={bodyPractitionerAddress.label}
                    value={bodyPractitionerAddress.value}
                />
            );
            contentSummaryRowTemplate.push(
                <SummaryRowTemplate
                    key={`bodyPractitionerDate-${index}`}
                    elementId={elementId}
                    caption={bodyPractitionerDate.label}
                    value={bodyPractitionerDate.value}
                />
            );
        }
    });

    return <>{contentSummaryRowTemplate}</>;
};

Examination.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    label: string
): ClaimFormSubmitFieldValue | ClaimFormSubmitFieldValue[] | undefined => {
    const submitValue: ClaimFormSubmitFieldValue[] = [];
    const valueObj = Examination.getValue(formValues, elementId);
    const { fieldSets } = valueObj;

    fieldSets.forEach((fieldSet: any, index: any) => {
        if (fieldSet.bodyPractitionerType.value) {
            const bodyPractitionerType = getClaimFormSubmitValue(
                'radiobuttons',
                `bodyPractitionerType-${index}`,
                fieldLabel.bodyPractitionerType,
                fieldSet.bodyPractitionerType.label,
                { string: fieldSet.bodyPractitionerType.value }
            );
            submitValue.push(bodyPractitionerType);
        }
        if (fieldSet.bodyPractitionerName.value) {
            const bodyPractitionerName = getClaimFormSubmitValue(
                'dropdown',
                `bodyPractitionerType-${index}`,
                fieldSet.bodyPractitionerName.label,
                fieldSet.bodyPractitionerName.value,
                { string: fieldSet.bodyPractitionerName.value }
            );
            submitValue.push(bodyPractitionerName);
        }
        if (fieldSet.bodyPractitionerAddress.value) {
            const bodyPractitionerDate = getClaimFormSubmitValue(
                'textfield',
                `bodyPractitionerAddress-${index}`,
                fieldSet.bodyPractitionerAddress.label,
                fieldSet.bodyPractitionerAddress.value,
                { string: fieldSet.bodyPractitionerAddress.value }
            );
            submitValue.push(bodyPractitionerDate);
        }
        if (fieldSet.bodyPractitionerDate.value) {
            const bodyPractitionerDate = getClaimFormSubmitValue(
                'datepicker',
                `bodyPractitionerDate-${index}`,
                fieldSet.bodyPractitionerDate.label,
                fieldSet.bodyPractitionerDate.value,
                { string: fieldSet.bodyPractitionerDate.value }
            );
            submitValue.push(bodyPractitionerDate);
        }
    });

    return submitValue;
};

Examination.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

Examination.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Examination.getValue(formValues, elementId);
    return value === undefined ? '' : value;
};

Examination.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    return Examination.getValue(formValues, elementId);
};

// This returns the component's isValid status
Examination.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    const valueObj = Examination.getValue(formValues, elementId);

    if (!valueObj) {
        return false;
    }

    const { fieldSets } = valueObj;

    if (!fieldSets) {
        return false;
    }

    return fieldSets.every((fieldSet: any) => {
        if (fieldSet.bodyPractitionerType.value === 'HOSPITAL') {
            return fieldSet.bodyPractitionerName.value && fieldSet.bodyPractitionerDate.value;
        } else {
            return (
                fieldSet.bodyPractitionerType.value &&
                fieldSet.bodyPractitionerName.value &&
                fieldSet.bodyPractitionerAddress.value &&
                fieldSet.bodyPractitionerDate.value
            );
        }
    });
};

Examination.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig || !fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }

    return Examination.isValid(formConfig, formValues, elementId);
};

export default Examination;
